import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Footer1 from "../navbar/Footer1";
import "./ProjectDetail.css";
import "./Works.css";

const ProjectDetail = () => {
  const { id, categoryId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllImages, setShowAllImages] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageOrientations, setImageOrientations] = useState({});

  const API_BASE_URL = "https://vogelkoparchitects.com/api/api";

  // Function to detect image orientation
  const detectImageOrientation = (url, index) => {
    const img = new Image();
    img.onload = () => {
      const orientation = img.naturalWidth > img.naturalHeight ? 'landscape' : 'portrait';
      setImageOrientations(prev => ({ ...prev, [index]: orientation }));
    };
    img.src = url;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (id) {
          const response = await fetch(`${API_BASE_URL}/project-detail/${id}/`);
          if (!response.ok) throw new Error("Failed to fetch project");
          const data = await response.json();
          setProject(data);
        } else if (categoryId) {
          const response = await fetch(`${API_BASE_URL}/projects/`);
          if (!response.ok) throw new Error("Failed to fetch projects");
          const data = await response.json();
          const filtered = data.filter(p => p.category && p.category.id === parseInt(categoryId));
          setProjects(filtered);
        }
        setLoading(false);
      } catch (err) {
        setError("Failed to load data");
        setLoading(false);
      }
    };
    fetchData();
  }, [id, categoryId]);

  // Detect orientations for all images when project loads
  useEffect(() => {
    if (project) {
      const allImages = [project.image, ...(project.project_images?.map(img => img.image) || [])].filter(Boolean);
      allImages.forEach((img, index) => {
        detectImageOrientation(img, index);
      });
    }
  }, [project]);

  if (loading) {
    return (
      <div className="project-loader">
        <div className="loader"></div>
        <p>Loading project details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="project-error">
        <p>{error}</p>
        <Link to="/works" className="back-link">← Back to Works</Link>
      </div>
    );
  }

  // Category Projects View
  if (categoryId && projects.length > 0) {
    const categoryName = projects[0]?.category?.title || "Category";
    return (
      <div className="works-page">
        {/* Hero Banner with Category Name in bottom-left corner */}
        <div className="works-hero">
          <div className="works-hero-bg" style={{ backgroundImage: projects[0]?.image ? `url(${projects[0].image})` : undefined }}>
            <div className="works-hero-overlay"></div>
          </div>
          <div className="works-hero-content" style={{ paddingBottom: "10px" }}> {/* Moved title lower */}
            <h1 className="works-hero-title">{categoryName}</h1>
          </div>
        </div>

        <div className="works-container">
          {/* Projects Grid - 3 cards per row matching Works.js */}
          <div className="works-grid">
            {projects.map((project) => (
              <div 
                key={project.id} 
                className="work-card"
                onClick={() => navigate(`/project-detail/${project.id}`)}
              >
                <div className="work-card-image">
                  <img
                    src={project.image || "/placeholder.jpg"}
                    alt={project.title}
                    onError={(e) => {
                      e.target.src = "/placeholder.jpg";
                    }}
                  />
                  {/* Title in bottom-left corner of card */}
                  <div className="card-title-overlay">
                    <h3 className="card-title">{project.title}</h3>
                  </div>
                  {/* Hover Overlay - Shows full description with arrow in bottom-right */}
                  <div className="work-card-overlay">
                    <div className="overlay-content">
                      <p className="overlay-description">
                        {project.description ? project.description : "No description available"}
                      </p>
                      {/* Diagonal Arrow - Bottom Right Corner */}
                      <div className="round-arrow">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="7" y1="17" x2="17" y2="7"></line>
                          <polyline points="7 7 17 7 17 17"></polyline>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <Footer1 />
      </div>
    );
  }

  // Single Project View
  if (!project) {
    return (
      <div className="project-not-found">
        <p>Project not found</p>
        <Link to="/works" className="back-link">← Back to Works</Link>
      </div>
    );
  }

  const allImages = [project.image, ...(project.project_images?.map(img => img.image) || [])].filter(Boolean);
  const displayImages = showAllImages ? allImages : allImages.slice(0, 6);
  const remainingImages = allImages.length - 6;

  return (
    <>
      {/* Hero Banner Section - Reduced Height with Title in Bottom-Left */}
      <div className="project-hero">
        <div className="project-hero-bg" style={{ backgroundImage: `url(${allImages[0]})` }}>
          <div className="project-hero-overlay"></div>
        </div>
        <div className="project-hero-content">
          <h1 className="project-hero-title">{project.title}</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="project-main">
        <div className="container">
          {/* Description Section - Left Aligned */}
          <div className="project-description-block">
            <p className="project-description-large"><i>"{project.description}"</i></p>
          </div>

          {/* Image and Info Row */}
          <div className="project-info-row">
            <div className="project-featured-image">
              <img 
                src={allImages[1] || allImages[0]} 
                alt={project.title}
                className={imageOrientations[1] === 'portrait' ? 'portrait-image' : 'landscape-image'}
              />
            </div>
            <div className="project-specs">
              <div className="specs-list">
                {project.client && project.client !== "Private Client" && (
                  <div className="spec-item">
                    <span className="spec-label">Client</span>
                    <span className="spec-value">{project.client}</span>
                  </div>
                )}
                {project.project_year && project.project_year !== "2025" && (
                  <div className="spec-item">
                    <span className="spec-label">Year</span>
                    <span className="spec-value">{project.project_year}</span>
                  </div>
                )}
                {project.location && project.location !== "India" && (
                  <div className="spec-item">
                    <span className="spec-label">Location</span>
                    <span className="spec-value">{project.location}</span>
                  </div>
                )}
                {project.category && (
                  <div className="spec-item">
                    <span className="spec-label">Category</span>
                    <span className="spec-value">{project.category.title}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Info Sections - No Tabs, Displayed One by One */}
          {project.site_context && (
            <div className="project-info-section">
              <h3 className="section-title">Site Context</h3>
              <div className="section-content" dangerouslySetInnerHTML={{ __html: project.site_context }} />
            </div>
          )}

          {project.program && (
            <div className="project-info-section">
              <h3 className="section-title">Program</h3>
              <div className="section-content" dangerouslySetInnerHTML={{ __html: project.program }} />
            </div>
          )}

          {project.architectural_response_and_climate && (
            <div className="project-info-section">
              <h3 className="section-title">Architectural Response and Climate</h3>
              <div className="section-content" dangerouslySetInnerHTML={{ __html: project.architectural_response_and_climate }} />
            </div>
          )}

          {project.materials_and_thermal_strategies && (
            <div className="project-info-section">
              <h3 className="section-title">Materials and Thermal Strategies</h3>
              <div className="section-content" dangerouslySetInnerHTML={{ __html: project.materials_and_thermal_strategies }} />
            </div>
          )}

          {/* Gallery Section - No Heading */}
          <div className="project-gallery-section">
            <div className="gallery-grid">
              {displayImages.map((img, index) => (
                <div 
                  key={index} 
                  className={`gallery-item ${!showAllImages && index === 5 && remainingImages > 0 ? 'gallery-item-last' : ''} ${imageOrientations[index] === 'portrait' ? 'portrait' : 'landscape'}`}
                  onClick={() => {
                    if (!showAllImages && index === 5 && remainingImages > 0) {
                      setShowAllImages(true);
                    } else {
                      setSelectedImage(img);
                    }
                  }}
                >
                  <img 
                    src={img} 
                    alt={`${project.title} view ${index + 1}`}
                    className={imageOrientations[index] === 'portrait' ? 'portrait-image' : 'landscape-image'}
                  />
                  {!showAllImages && index === 5 && remainingImages > 0 && (
                    <div className="gallery-see-all">
                      <span>+{remainingImages}</span>
                      <p>View All Photos</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox" onClick={() => setSelectedImage(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setSelectedImage(null)}>×</button>
            <img src={selectedImage} alt="Full size view" />
          </div>
        </div>
      )}

      <Footer1 />
    </>
  );
};

export default ProjectDetail;
